/**
 * Public-site vote widget. Read-only display of weighted + raw
 * scores, with an "Open in Axon to vote" CTA — the public site
 * doesn't have its own daemon to sign with, and SIWE-via-browser-
 * wallet is deferred to v1.3.
 *
 * If the marketplace-api isn't reachable, we render a quiet "—" so
 * the rest of the card stays usable.
 */
import { useEffect, useState } from "react";
import { getScore, type AggregateScore } from "../lib/marketplace-api.js";
import { LOCAL_DASHBOARD_URL } from "../lib/daemon.js";

export interface VoteWidgetProps {
  strategyId: string;
  variant?: "compact" | "detail";
}

export function VoteWidget({ strategyId, variant = "compact" }: VoteWidgetProps) {
  const [score, setScore] = useState<AggregateScore | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getScore(strategyId)
      .then((s) => {
        if (!cancelled) setScore(s);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [strategyId]);

  const weighted = score?.weightedScore ?? 0;
  const raw = score?.rawScore ?? 0;

  if (variant === "detail") {
    return (
      <div className="vw-detail">
        <div className="vw-detail-num">
          <span className="vw-num-big">{score ? fmtScore(weighted) : "—"}</span>
          <span className="vw-num-lab">weighted</span>
        </div>
        {score && (
          <div className="vw-detail-raw">
            <span className="vw-raw-pos">+{score.upvotes}</span>
            <span className="vw-raw-sep">/</span>
            <span className="vw-raw-neg">−{score.downvotes}</span>
            <span className="vw-raw-lab">raw</span>
          </div>
        )}
        <a
          className="vw-cta"
          href={`${LOCAL_DASHBOARD_URL}/?install=${encodeURIComponent(strategyId)}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Open in Axon to vote →
        </a>
        {error && <p className="vw-err">Voting backend unavailable. ({error})</p>}
        <Styles />
      </div>
    );
  }

  return (
    <span className="vw-compact" aria-label={`Score: ${fmtScore(weighted)} (${raw} raw)`}>
      <span className={"vw-cscore " + (raw < 0 ? "neg" : raw > 0 ? "pos" : "")}>
        {score ? fmtScore(weighted) : "—"}
      </span>
      <Styles />
    </span>
  );
}

function fmtScore(n: number): string {
  if (n === 0) return "0";
  const sign = n > 0 ? "+" : "−";
  const abs = Math.abs(n);
  if (abs >= 1000) return `${sign}${(abs / 1000).toFixed(1)}k`;
  if (abs >= 10) return `${sign}${abs.toFixed(0)}`;
  return `${sign}${abs.toFixed(1)}`;
}

function Styles() {
  return (
    <style>{`
      .vw-compact { display: inline-flex; align-items: center;
        font-family: var(--at-font-mono); font-variant-numeric: tabular-nums; }
      .vw-cscore { font-size: 11px; font-weight: 600; color: var(--at-fg);
        padding: 2px 8px; border-radius: var(--at-radius-full);
        background: var(--at-bg); border: 1px solid var(--at-hairline); }
      .vw-cscore.pos { color: var(--at-accent); border-color: rgba(74,222,128,0.22); background: rgba(74,222,128,0.06); }
      .vw-cscore.neg { color: var(--at-danger); border-color: rgba(248,113,113,0.22); background: rgba(248,113,113,0.06); }

      .vw-detail { display: flex; flex-direction: column; gap: 8px;
        padding: 12px;
        background: var(--at-bg); border: 1px solid var(--at-hairline);
        border-radius: var(--at-radius-md); }
      .vw-detail-num { display: flex; align-items: baseline; gap: 6px;
        font-family: var(--at-font-mono); }
      .vw-num-big { font-size: 22px; font-weight: 700; color: var(--at-fg);
        font-variant-numeric: tabular-nums; }
      .vw-num-lab { font-size: 9px; letter-spacing: 0.18em; text-transform: uppercase;
        color: var(--at-muted); }
      .vw-detail-raw { display: flex; align-items: baseline; gap: 4px;
        font-family: var(--at-font-mono); font-size: 11px; color: var(--at-muted); }
      .vw-raw-pos { color: var(--at-accent); }
      .vw-raw-neg { color: var(--at-danger); }
      .vw-raw-sep { color: var(--at-hairline-strong); }
      .vw-raw-lab { font-size: 9px; letter-spacing: 0.18em; text-transform: uppercase; margin-left: 6px; }

      .vw-cta { display: inline-block; font-size: 11px; font-family: var(--at-font-mono);
        color: var(--at-accent); text-decoration: none; margin-top: 2px;
        padding: 4px 0; }
      .vw-cta:hover { text-decoration: underline; }

      .vw-err { margin: 0; font-size: 10px; color: var(--at-muted); font-style: italic; }
    `}</style>
  );
}
